import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { AppWindow, CheckCircle2, Droplets, Layers, MessageCircle, PlugZap, Tag } from "lucide-react";
import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/SectionHeading";
import { CTABanner } from "@/components/CTABanner";
import { SERVICES } from "@/data/content";
import { waLink } from "@/lib/site";

const ICONS = { "hollow-tile-tapping": Layers, "water-ponding": Droplets, "blue-tape-marking": Tag, "window-checks": AppWindow, "electrical-testing": PlugZap };

export default function Services() {
  const { hash } = useLocation();
  const [active, setActive] = useState(SERVICES[0].id);

  useEffect(() => {
    if (hash && SERVICES.some((s) => s.id === hash.slice(1))) setActive(hash.slice(1));
  }, [hash]);

  const service = SERVICES.find((s) => s.id === active);
  const Icon = ICONS[service.id];

  return (
    <>
      <SEO
        title="Inspection Services | Hollow-Tile Tapping, Ponding Tests & More — Lobang"
        description="Hollow-tile tapping, water ponding tests, blue-tape defect marking, window checks and electrical point testing for new BTO flats, condos and landed homes in Singapore."
      />
      <PageHero
        eyebrow="Our Services"
        title="Every corner checked, every defect flagged"
        copy="Five inspection pillars, one signature scope — the same uncompromising standard whether yours is a 3-room BTO or a landed home."
      />

      <section className="px-5 py-16 sm:px-8 sm:py-24" data-testid="services-tabs-section">
        <div className="mx-auto max-w-7xl">
          <div className="flex gap-2 overflow-x-auto pb-2 sm:flex-wrap sm:justify-center" role="tablist" aria-label="Inspection services">
            {SERVICES.map((s) => {
              const TabIcon = ICONS[s.id];
              const isActive = s.id === active;
              return (
                <button
                  key={s.id}
                  role="tab"
                  aria-selected={isActive}
                  data-testid={`service-tab-${s.id}`}
                  onClick={() => setActive(s.id)}
                  className={`flex shrink-0 items-center gap-2 rounded-full border px-5 py-2.5 text-xs font-bold uppercase tracking-wider transition-all duration-300 ${
                    isActive
                      ? "border-gold bg-gold text-white shadow-luxury"
                      : "border-border bg-white text-charcoal-soft hover:border-gold/50 hover:text-gold-dark"
                  }`}
                >
                  <TabIcon className="h-4 w-4" />
                  {s.title}
                </button>
              );
            })}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="mt-10 grid items-center gap-10 rounded-[2.5rem] border border-border bg-white p-6 shadow-luxury sm:p-10 lg:grid-cols-2"
              data-testid="service-panel"
            >
              <div className="gold-ring order-2 overflow-hidden rounded-[1.75rem] lg:order-1">
                <img src={service.image} alt={service.title} className="h-72 w-full object-cover sm:h-96" loading="lazy" />
              </div>
              <div className="order-1 lg:order-2">
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-cream text-gold-dark">
                  <Icon className="h-6 w-6" />
                </span>
                <h2 className="mt-5 font-serif text-3xl font-semibold text-charcoal">{service.title}</h2>
                <p className="mt-1 font-serif text-base italic text-gold-dark">{service.tagline}</p>
                <p className="mt-4 text-sm leading-relaxed text-charcoal-soft sm:text-base">{service.desc}</p>
                <ul className="mt-6 space-y-3">
                  {service.checks.map((c) => (
                    <li key={c} className="flex items-start gap-3 text-sm text-charcoal-soft">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
                      {c}
                    </li>
                  ))}
                </ul>
                <a
                  href={waLink(`Hi Lobang! I'd like to ask about your ${service.title} service.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`service-panel-whatsapp-${service.id}`}
                  className="mt-8 inline-flex items-center gap-2 rounded-full bg-wa px-6 py-3.5 text-sm font-bold text-white shadow-wa transition-transform duration-300 hover:scale-[1.04]"
                >
                  <MessageCircle className="h-4 w-4" />
                  Ask about this service
                </a>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </section>

      <CTABanner
        title="One scope. Zero compromises."
        copy="Every inspection includes all five pillars as standard. WhatsApp us your key collection date to lock in your slot."
      />
    </>
  );
}
